import { Box, Button, Chip, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import {
  EMPTY_GUEST_FILTER,
  GUEST_STATUS_LABELS,
  filterSummary,
  isFiltering,
  type GuestFilter,
  type GuestStatus,
} from '../../../lib/guestFilter';
import { sortTags, tagColor } from '../../../lib/guestTags';
import type { GuestTag } from '../../../services/tracker-api-client';

const STATUSES = Object.keys(GUEST_STATUS_LABELS) as GuestStatus[];

/**
 * Narrowing 137 people down to the ones being dealt with right now.
 *
 * Always on screen rather than behind a disclosure: a filter a host cannot see
 * is a filter they forget is on, and a roster that silently shows 12 of 137 is
 * worse than one that shows all of them. The count beside it names both numbers
 * for the same reason.
 *
 * Status is multi-select because the useful questions are plural — "going or
 * maybe" is the list you cater for, and neither chip alone answers it.
 */
export default function GuestFilterBar({
  filter,
  onChange,
  hotels,
  shown,
  total,
  tonight,
  tags,
  tagCounts,
  bulkLabel,
  bulkDisabled,
  onBulkTag,
}: {
  filter: GuestFilter;
  onChange: (next: GuestFilter) => void;
  /** Hotels present on the roster, for the staying-at menu. */
  hotels: string[];
  shown: number;
  total: number;
  /** Tonight in the trip's timezone, `YYYY-MM-DD`. */
  tonight: string;
  /**
   * The trip's tag vocabulary, or null when this API has no tags.
   *
   * Null hides the whole tag row and the bulk button. A chip that filters
   * nothing and a button that writes nothing are worse than their absence —
   * the host spends the afternoon believing they tagged the roster.
   */
  tags: GuestTag[] | null;
  /** Trip-wide counts by tag id, so a chip says how many it would leave. */
  tagCounts: Record<string, number>;
  /** "Tag these 12…" or "Tag 8 selected…" — {@link bulkTarget} decides. */
  bulkLabel: string;
  /**
   * True when the target is empty. Keyed on the TARGET, not on `shown`: with
   * rows ticked and a filter that shows nothing, there is still a cohort to tag
   * and disabling the button would strand it.
   */
  bulkDisabled: boolean;
  onBulkTag: () => void;
}) {
  const toggleTag = (tagId: string) => {
    const on = filter.tagIds.includes(tagId);
    onChange({
      ...filter,
      tagIds: on ? filter.tagIds.filter((id) => id !== tagId) : [...filter.tagIds, tagId],
      // "Carries this tag" and "carries none" together match nobody, so picking
      // a tag turns untagged off rather than leaving a filter that answers 0.
      withoutTags: false,
    });
  };

  const toggleStatus = (status: GuestStatus) => {
    const on = filter.statuses.includes(status);
    onChange({
      ...filter,
      statuses: on ? filter.statuses.filter((s) => s !== status) : [...filter.statuses, status],
    });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          placeholder="Search name, email, phone or hotel"
          value={filter.query}
          onChange={(event) => onChange({ ...filter, query: event.target.value })}
          sx={{ flexGrow: 1, minWidth: 200 }}
        />
        {hotels.length > 0 ? (
          <TextField
            select
            size="small"
            label="Staying at"
            value={filter.stayingAt[0] ?? ''}
            onChange={(event) =>
              onChange({ ...filter, stayingAt: event.target.value ? [event.target.value] : [] })
            }
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">Anywhere</MenuItem>
            {hotels.map((hotel) => (
              <MenuItem key={hotel} value={hotel}>{hotel}</MenuItem>
            ))}
            <MenuItem value="none">No hotel on file</MenuItem>
          </TextField>
        ) : null}
      </Stack>

      <Stack direction="row" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }} alignItems="center">
        {STATUSES.map((status) => (
          <Chip
            key={status}
            size="small"
            label={GUEST_STATUS_LABELS[status]}
            color={filter.statuses.includes(status) ? 'primary' : 'default'}
            variant={filter.statuses.includes(status) ? 'filled' : 'outlined'}
            onClick={() => toggleStatus(status)}
          />
        ))}
        {/* The night, not a date picker: "who is here tonight" is the question. */}
        {tonight ? (
          <Chip
            size="small"
            label="Here tonight"
            color={filter.hereOn ? 'primary' : 'default'}
            variant={filter.hereOn ? 'filled' : 'outlined'}
            onClick={() => onChange({ ...filter, hereOn: filter.hereOn ? '' : tonight })}
          />
        ) : null}

        <Box sx={{ flexGrow: 1 }} />

        <Typography variant="caption" color="text.secondary">
          {filterSummary(shown, total)}
        </Typography>
        {isFiltering(filter) ? (
          <Button size="small" onClick={() => onChange({ ...EMPTY_GUEST_FILTER })}>Clear</Button>
        ) : null}
      </Stack>

      {/*
        The tag row and the bulk button, which are one thought: narrow to a
        cohort, then tag all of it. That is three taps on a phone, where ticking
        forty rows on a 375px screen is not something anyone finishes — which is
        why the button sits on the FILTER bar and not on the table.
      */}
      {tags ? (
        <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }} alignItems="center">
          {sortTags(tags).map((tag) => {
            const on = filter.tagIds.includes(tag.id);
            const count = tagCounts[tag.id] ?? 0;
            /*
              A critical tag reads as critical HERE TOO. This row is a second
              rendering of the same vocabulary the chip column draws, and it
              dropped the one distinction that column exists to protect: an
              allergy has to outrank a colour a host picked for "college
              friends", or the filter row is the surface that teaches them the
              outline means nothing.
            */
            const ink = tag.isCritical ? 'var(--tsb-danger-ink)' : tagColor(tag);
            return (
              <Chip
                key={tag.id}
                size="small"
                label={`${tag.label} ${count}`}
                variant={on ? 'filled' : 'outlined'}
                onClick={() => toggleTag(tag.id)}
                aria-pressed={on}
                sx={{
                  cursor: 'pointer',
                  borderColor: ink,
                  ...(on ? { bgcolor: ink, color: '#fff' } : { color: ink }),
                }}
              />
            );
          })}
          <Tooltip title="Guests with no tag at all — how you finish a tagging pass">
            <Chip
              size="small"
              label="Untagged"
              variant={filter.withoutTags ? 'filled' : 'outlined'}
              color={filter.withoutTags ? 'primary' : 'default'}
              aria-pressed={filter.withoutTags}
              onClick={() =>
                onChange({
                  ...filter,
                  withoutTags: !filter.withoutTags,
                  // Same contradiction, from the other side.
                  tagIds: filter.withoutTags ? filter.tagIds : [],
                })
              }
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>

          <Box sx={{ flexGrow: 1 }} />

          <Button size="small" variant="outlined" disabled={bulkDisabled} onClick={onBulkTag}>
            {bulkLabel}
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
}
